import { asText } from '@prismicio/richtext';
import { FinePrint } from 'components/fine-print/FinePrint';
import { GridLayout, GridLayoutItem } from 'components/grid/GridLayout';
import { H2 } from 'components/heading/Heading';
import PowerPackageCard from 'components/power-package-card/PowerPackageCard';
import PricesItem from 'components/prices/PricesItem';
import {
  PageSlicesPrice_And_Card,
  PageSlicesPrice_And_CardDefaultItems,
} from 'prismic-types';
import { PrismicImage } from 'prismic/types/image';
import { PrismicLink, PrismicLinkWithMeta } from 'prismic/types/link';
import { PrismicRichText } from 'prismic/types/richtext';

import { useIntl } from 'react-intl';

import style from './PriceAndCard.module.scss';

export type PriceAndCardSlice = PageSlicesPrice_And_Card;
type PriceItems = PageSlicesPrice_And_CardDefaultItems[];

type Props = {
  title?: PrismicRichText;
  finePrint?: PrismicRichText;
  cardTitle?: string;
  cardImage?: PrismicImage;
  cardContent?: PrismicRichText;
  cardButtonText?: string;
  cardLinkTo?: PrismicLink;
  items?: PriceItems;
};

export const PriceAndCardSlice = ({
  title,
  finePrint,
  cardTitle,
  cardImage,
  cardContent,
  cardButtonText,
  cardLinkTo,
  items,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();

  const firstPriceItem = items?.[0];
  const secondPriceItem = items?.[1];

  return (
    <GridLayout>
      <GridLayoutItem
        columns={{ desktop: 4, tablet: 6, mobile: 10 }}
        offset={{ desktop: 1, tablet: 0 }}
      >
        <div className={style.layout}>
          <div className={style.title}>{title && <H2>{asText(title)}</H2>}</div>
          <div className={style.content}>
            <div className={style.priceContainer}>
              <PricesItem
                title={firstPriceItem?.price_title || ''}
                number={firstPriceItem?.price_cost?.toString()}
                unit={firstPriceItem?.price_unit || ''}
              />
              <PricesItem
                title={secondPriceItem?.price_title || ''}
                number={secondPriceItem?.price_cost?.toString()}
                unit={secondPriceItem?.price_unit || ''}
              />
            </div>
            <FinePrint text={finePrint} />
          </div>
        </div>
      </GridLayoutItem>
      <GridLayoutItem
        columns={{ desktop: 5, tablet: 6 }}
        offset={{ desktop: 6, tablet: 6 }}
      >
        {cardImage && (
          <PowerPackageCard
            item={{
              package_name: cardTitle || '',
              description: cardContent || [],
              short_description: '',
              package_image: cardImage,
              first_price_title: firstPriceItem?.price_title || '',
              package_first_price: firstPriceItem?.price_cost || 0,
              second_price_title: secondPriceItem?.price_title || '',
              package_second_price: secondPriceItem?.price_cost || 0,
              velja: cardLinkTo as PrismicLinkWithMeta,
              linkText: formatMessage({
                id: 'buy',
                defaultMessage: cardButtonText,
              }),
              _meta: {
                uid: '',
              },
            }}
            greyTheme
          />
        )}
      </GridLayoutItem>
    </GridLayout>
  );
};

export function prismicSliceToPriceAndCard(s: PriceAndCardSlice) {
  return (
    <PriceAndCardSlice
      title={s.variation?.primary?.title ?? undefined}
      finePrint={s.variation?.primary?.fine_print ?? undefined}
      cardTitle={s.variation?.primary?.card_title ?? undefined}
      cardImage={s.variation?.primary?.card_image ?? undefined}
      cardContent={s.variation?.primary?.card_content ?? undefined}
      cardButtonText={s.variation?.primary?.card_button_text ?? undefined}
      cardLinkTo={s.variation?.primary?.card_link_to ?? undefined}
      items={s.variation?.items ?? []}
    />
  );
}
